// Settings, Activity log (spec C14, C15, Admin only; docs/plans/sign-in.md, PR 10): every action in
// the portal, read only. Markup from the Settings board's Activity log tab: the list toolbar (Date,
// Group by, Collapse all, Filters, Clear all), group cards and the six-column table.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLoaderData, useSearchParams } from 'react-router';
import type { ActivityEntry, ActivityView } from '../auth/api';
import { PageHeader } from '../shell/PageHeader';
import {
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronsInIcon,
  ChevronsOutIcon,
  FilterLinesIcon,
  XIcon,
} from '../shell/icons';
import {
  activityCopy as c,
  appliedLabels,
  checkTyped,
  countLabel,
  groupOptions,
  groupRows,
  latestLabel,
  longValue,
  noFilters,
  passes,
  peopleOptions,
  presetOf,
  presets,
  rangeLabel,
  removeApplied,
  roleChips,
  toggle,
  typedDay,
  typeGroups,
  when,
  type Filters,
  type GroupBy,
  type Range,
} from '../settings/activity-lines';
import { SettingsNav } from '../settings/SettingsNav';

export type ActivityLoaderData = ActivityView;

// When, Who, Record, Action, Before, After (the board's widths, without fixed minimums).
const columns =
  'minmax(0, 0.7fr) minmax(0, 0.9fr) minmax(0, 1.2fr) minmax(0, 1.8fr) minmax(0, 1fr) minmax(0, 1fr)';

type Open = 'range' | 'group' | 'filters' | null;

export function ActivityPage() {
  const view = useLoaderData() as ActivityLoaderData;
  const [, setSearch] = useSearchParams();
  const today = view.today;
  const [open, setOpen] = useState<Open>(null);
  const [group, setGroup] = useState<GroupBy>('day');
  const [collapsed, setCollapsed] = useState<string[]>([]);
  const [filters, setFilters] = useState<Filters>(noFilters);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const rows = useMemo(() => view.entries.filter((e) => passes(e, filters)), [view, filters]);
  const groups = groupRows(rows, group, view, today);
  const grouped = group !== 'none';
  const applied = appliedLabels(filters);
  const anyOpen = groups.some((g) => !collapsed.includes(g.key));

  const pickRange = (range: Range) => setSearch(range, { replace: true });
  const toggleOpen = (which: Exclude<Open, null>) => setOpen(open === which ? null : which);

  return (
    <>
      <PageHeader title="Settings" subtitle="Changes apply to everyone in the department" />
      <div className="setwrap">
        <SettingsNav current="/settings/activity" />
        <div className="setcol">
          <div className="row g12 between">
            <div className="col g4">
              <h2 className="dtitle">{c.title}</h2>
              <span className="t12 muted">{c.sub}</span>
            </div>
          </div>
          <div className="col g8 altb">
            <div className="ltb">
              <div className="row g8">
                <Popover
                  open={open === 'range'}
                  onClose={() => setOpen(null)}
                  scrimLabel="Close date range"
                  button={
                    <button
                      type="button"
                      className="chip"
                      aria-haspopup="dialog"
                      aria-expanded={open === 'range'}
                      onClick={() => toggleOpen('range')}
                    >
                      <CalendarIcon className="ico s14" />
                      <span className="muted">{c.date}</span>
                      <span className="ink">{rangeLabel(view, today)}</span>
                      <ChevronDownIcon className="ico s12 muted" />
                    </button>
                  }
                >
                  <RangePopover
                    range={view}
                    today={today}
                    onPick={pickRange}
                    onClose={() => setOpen(null)}
                  />
                </Popover>
              </div>
              <div className="ltb-r">
                <Popover
                  open={open === 'group'}
                  onClose={() => setOpen(null)}
                  scrimLabel="Close group by"
                  button={
                    <button
                      type="button"
                      className={grouped ? 'chip soft' : 'chip'}
                      aria-haspopup="menu"
                      aria-expanded={open === 'group'}
                      onClick={() => toggleOpen('group')}
                    >
                      <span className="muted">{c.groupBy}</span>
                      <span className="ink">
                        {groupOptions.find((g) => g.key === group)!.label}
                      </span>
                      <ChevronDownIcon className="ico s12 muted" />
                    </button>
                  }
                >
                  <div
                    className="pop p-4"
                    role="menu"
                    aria-label={c.groupBy}
                    style={{ width: 'max-content' }}
                  >
                    {groupOptions.map((g) => (
                      <button
                        key={g.key}
                        type="button"
                        className="mi"
                        role="menuitemradio"
                        aria-checked={group === g.key}
                        onClick={() => {
                          setGroup(g.key);
                          setCollapsed([]);
                          setOpen(null);
                        }}
                      >
                        {group === g.key ? (
                          <CheckIcon className="ico ck" />
                        ) : (
                          <span className="sp" />
                        )}
                        {g.label}
                      </button>
                    ))}
                  </div>
                </Popover>
                {grouped && rows.length > 0 && (
                  <button
                    type="button"
                    className="chip"
                    onClick={() => setCollapsed(anyOpen ? groups.map((g) => g.key) : [])}
                  >
                    {anyOpen ? (
                      <ChevronsInIcon className="ico s14" />
                    ) : (
                      <ChevronsOutIcon className="ico s14" />
                    )}
                    {anyOpen ? c.collapseAll : c.expandAll}
                  </button>
                )}
                <Popover
                  open={open === 'filters'}
                  onClose={() => setOpen(null)}
                  scrimLabel="Close filters"
                  button={
                    <button
                      type="button"
                      className={open === 'filters' || applied.length ? 'chip soft' : 'chip'}
                      aria-haspopup="dialog"
                      aria-expanded={open === 'filters'}
                      onClick={() => toggleOpen('filters')}
                    >
                      <FilterLinesIcon className="ico s14" />
                      {c.filters}
                      {applied.length > 0 && <span className="badge">{applied.length}</span>}
                    </button>
                  }
                >
                  <FiltersPopover
                    view={view}
                    filters={filters}
                    onChange={setFilters}
                    onReset={() => {
                      setFilters(noFilters);
                      pickRange(presets[1].range!(today));
                    }}
                    onClose={() => setOpen(null)}
                  />
                </Popover>
                {applied.length > 0 && (
                  <button
                    type="button"
                    className="linkbtn tb-clear"
                    onClick={() => setFilters(noFilters)}
                  >
                    {c.clearAll}
                  </button>
                )}
              </div>
            </div>
            {applied.length > 0 && (
              <div className="row g8 wrap" aria-label="Applied filters">
                {applied.map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    className="chip chip-sm"
                    aria-label={`Remove filter ${a.label}`}
                    onClick={() => setFilters(removeApplied(filters, a.key))}
                  >
                    {a.label}
                    <XIcon className="ico x" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="hdr" style={{ gridTemplateColumns: columns }}>
            {c.columns.map((h) => (
              <div key={h} className="th">
                <span className="sort">
                  <span className="ell">{h}</span>
                </span>
              </div>
            ))}
          </div>
          {rows.length === 0 ? (
            <div className="gcard empty">
              <span className="t13 w5 ink">{c.empty}</span>
              {applied.length > 0 && (
                <button type="button" className="btn btn-sm" onClick={() => setFilters(noFilters)}>
                  {c.clearAll}
                </button>
              )}
            </div>
          ) : (
            groups.map((g) => {
              const isOpen = !grouped || !collapsed.includes(g.key);
              return (
                <div key={g.key} className="gcard">
                  {grouped && (
                    <button
                      type="button"
                      className={isOpen ? 'ghead' : 'ghead closed'}
                      aria-expanded={isOpen}
                      onClick={() => setCollapsed(toggle(collapsed, g.key))}
                    >
                      <ChevronDownIcon
                        className={isOpen ? 'ico s14 chev' : 'ico s14 chev closed'}
                      />
                      <span className="glabel">{g.label}</span>
                      {g.sub && <span className="t12 muted">{g.sub}</span>}
                      <span className="tag gcount">{countLabel(g.rows.length)}</span>
                      <span className="ml-auto t12 muted">{latestLabel(g.rows[0])}</span>
                    </button>
                  )}
                  {isOpen && (
                    <div>
                      {g.rows.map((e) => (
                        <ActivityRow key={e.id} entry={e} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
          {view.truncated && <p className="esub">{c.truncated(view.entries.length)}</p>}
          <div className="lfoot">{c.showing(rows.length, view.entries.length)}</div>
        </div>
      </div>
    </>
  );
}

const twoLines = {
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
} as const;

function ActivityRow({ entry: e }: { entry: ActivityEntry }) {
  const w = when(e.at);
  return (
    <div
      className="trow"
      style={{ display: 'grid', gridTemplateColumns: columns, cursor: 'default' }}
    >
      <div className="td vis" style={twoLines}>
        <span className="mono ell text2">{w.date}</span>
        <span className="t12 muted mono">{w.time}</span>
      </div>
      <div className="td ink">
        <span className="ell">{e.who}</span>
      </div>
      {/* Records arrive with renewals; settings and sign-ins have none. */}
      <div className="td vis">
        <span className="faint">-</span>
      </div>
      <div className="td vis" style={twoLines}>
        <span className="tag t-grey">{e.type}</span>
        <span className="t12 ink ell">{e.action}</span>
      </div>
      <div className="td vis text2">
        <Value text={e.before} />
      </div>
      <div className="td vis ink">
        <Value text={e.after} />
      </div>
    </div>
  );
}

function Value({ text }: { text: string }) {
  if (!text) return <span className="ell">-</span>;
  if (text.length <= longValue) return <span className="ell">{text}</span>;
  return (
    <span className="tip tipcell" tabIndex={0}>
      <span className="ell">{text}</span>
      <span className="tipbox" role="tooltip">
        {text}
      </span>
    </span>
  );
}

function Popover({
  open,
  onClose,
  scrimLabel,
  button,
  children,
}: {
  open: boolean;
  onClose: () => void;
  scrimLabel: string;
  button: ReactNode;
  children: ReactNode;
}) {
  return (
    <div style={{ position: 'relative' }}>
      {button}
      {open && (
        <>
          <button type="button" className="pop-scrim" aria-label={scrimLabel} onClick={onClose} />
          {children}
        </>
      )}
    </div>
  );
}

function RangePopover({
  range,
  today,
  onPick,
  onClose,
}: {
  range: Range;
  today: string;
  onPick: (range: Range) => void;
  onClose: () => void;
}) {
  const [custom, setCustom] = useState(false);
  const [from, setFrom] = useState(typedDay(range.from));
  const [to, setTo] = useState(typedDay(range.to));
  const [error, setError] = useState<string | null>(null);
  const current = custom ? 'custom' : presetOf(range, today);

  function type(nextFrom: string, nextTo: string) {
    setFrom(nextFrom);
    setTo(nextTo);
    setCustom(true);
    const checked = checkTyped(nextFrom, nextTo);
    if ('error' in checked) {
      setError(checked.error);
    } else {
      setError(null);
      onPick(checked);
    }
  }

  return (
    <div className="pop alpop-l" role="dialog" aria-labelledby="alr-t">
      <div className="pop-h">
        <span id="alr-t" className="t14 w6">
          {c.date}
        </span>
        <button type="button" className="xbtn" aria-label="Close" onClick={onClose}>
          <XIcon />
        </button>
      </div>
      <div className="pop-b">
        <div className="fopts" role="group" aria-label="Preset">
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              className={current === p.key ? 'chip chip-sm on' : 'chip chip-sm'}
              aria-pressed={current === p.key}
              onClick={() => {
                if (!p.range) {
                  setCustom(true);
                  return;
                }
                onPick(p.range(today));
                onClose();
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="sf2">
          <div>
            <label className="flab" htmlFor="alr-from">
              {c.from}
            </label>
            <input
              id="alr-from"
              className="input"
              type="text"
              autoComplete="off"
              placeholder="Nov 6, 2026"
              aria-invalid={error ? true : undefined}
              value={from}
              onChange={(e) => type(e.target.value, to)}
            />
          </div>
          <div>
            <label className="flab" htmlFor="alr-to">
              {c.to}
            </label>
            <input
              id="alr-to"
              className="input"
              type="text"
              autoComplete="off"
              placeholder="Nov 12, 2026"
              aria-invalid={error ? true : undefined}
              value={to}
              onChange={(e) => type(from, e.target.value)}
            />
          </div>
        </div>
        {error && (
          <p className="ferr m0" role="alert">
            {error}
          </p>
        )}
        <p className="fnote m0">{c.typing}</p>
      </div>
    </div>
  );
}

function FiltersPopover({
  view,
  filters,
  onChange,
  onReset,
  onClose,
}: {
  view: ActivityView;
  filters: Filters;
  onChange: (f: Filters) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const people = peopleOptions(view, search);
  return (
    <div className="pop fpop" role="dialog" aria-labelledby="alf-t">
      <div className="pop-h">
        <span id="alf-t" className="t14 w6">
          {c.filters}
        </span>
        <button type="button" className="xbtn" aria-label="Close filters" onClick={onClose}>
          <XIcon />
        </button>
      </div>
      <div className="pop-b">
        <div>
          <label className="flab" htmlFor="alf-pq">
            {c.person}
          </label>
          <input
            id="alf-pq"
            className="input"
            type="search"
            placeholder={c.searchPeople}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="clist">
            {people.map((p) => (
              <label key={p.name} className="crow">
                <input
                  type="checkbox"
                  checked={filters.people.includes(p.name)}
                  onChange={() => onChange({ ...filters, people: toggle(filters.people, p.name) })}
                />
                <span>{p.name}</span>
                <span className="t12 muted">{p.sub}</span>
                <span className="cnt">{p.count}</span>
              </label>
            ))}
            {people.length === 0 && <div className="t12 muted p-8">{c.noPeople}</div>}
          </div>
        </div>
        <div role="group" aria-labelledby="alf-a">
          <div id="alf-a" className="flab">
            {c.actionType}
          </div>
          <div className="clist">
            {typeGroups.map((g) => (
              <div key={g.label}>
                <div className="vm-sec">{g.label}</div>
                {g.types.map((t) => (
                  <label key={t} className="crow">
                    <input
                      type="checkbox"
                      checked={filters.types.includes(t)}
                      onChange={() => onChange({ ...filters, types: toggle(filters.types, t) })}
                    />
                    <span>{t}</span>
                    <span className="cnt">{view.entries.filter((e) => e.type === t).length}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div role="group" aria-labelledby="alf-r">
          <div id="alf-r" className="flab">
            {c.role}
          </div>
          <div className="fopts">
            {roleChips.map((r) => {
              const on = filters.roles.includes(r.code);
              return (
                <button
                  key={r.code}
                  type="button"
                  className={on ? 'chip chip-sm on' : 'chip chip-sm'}
                  aria-pressed={on}
                  onClick={() => onChange({ ...filters, roles: toggle(filters.roles, r.code) })}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="pop-f">
        <button
          type="button"
          className="linkbtn text2"
          style={{ textDecoration: 'none' }}
          onClick={onReset}
        >
          {c.reset}
        </button>
        <button type="button" className="btn btn-primary" onClick={onClose}>
          {c.apply}
        </button>
      </div>
    </div>
  );
}
